<template>
  <div
    v-if="src"
    v-el-resize="scale ? null : debouncedOnResize"
    class="absolute-fill card-background"
    v-bind:class="{ loaded }"
  >
    <img
      v-if="!src.endsWith('.mp4')"
      class="absolute-fill"
      width="100%" height="100%" loading="lazy"
      :src="src"
      @load="loaded = true"
    />
    <template v-else-if="scaled">
      <video
        v-if="tryVideo"
        ref="video"
        class="absolute-fill"
        width="100%" height="100%" playsinline autoplay muted loop loading="lazy"
        :poster="poster"
        :src="scaled.src"
        @loadeddata="loaded = true"
      />
      <picture v-else>
        <!-- Probably iOS in low-power mode -->
        <source type="video/mp4" :srcset="scaled.src">
        <img
          class="absolute-fill"
          width="100%" height="100%" type="image/jpeg" loading="lazy"
          :src="poster"
          @load="loaded = true"
        />
      </picture>
    </template>
  </div>
</template>

<style scoped>
.card-background {
  opacity: .01;
}
.card-background.loaded {
  opacity: 1;
  transition: opacity .4s ease-in-out;
}
.card-background img,
.card-background video {
  object-fit: cover;
}
</style>

<script>
const SCALES = [ [ 160, 90 ], [ 320, 180 ], [ 480, 270 ], [ 640, 360 ], [ 1280, 720 ] ];
const PAGE_BACKGROUND_REGEX = /^https:\/\/cdn\.ellacard\.com\/page_backgrounds\/[^\.]+\.mp4$/;

export default {
  name: 'card-background',

  props: [ 'src', 'scale' ],

  data() {
    return {
      tryVideo: true,
      loaded: false,
      bounds: null,
      scaled: null
    };
  },

  computed: {
    poster() {
      return this.src.replace(/mp4$/, PAGE_BACKGROUND_REGEX.test(this.src) ? 'poster.jpg' : 'webp');
    },

    updateCondition() {
      return [ this.src, this.scale, this.bounds ];
    }
  },

  created() {
    this.debouncedOnResize = _.debounce(x => this.bounds = x, 3000, { leading: true, trailing: true });
  },

  methods: {
    update() {
      if (!this.src?.endsWith('.mp4'))
        // Image sources do not need to auto-scale
        return;

      if (!PAGE_BACKGROUND_REGEX.test(this.src)) {
        this.scaled = { src: this.src };
        return;
      }

      if (this.scale) {
        const [ w, h ] = this.scale;
        this.scaled = { src: this.src.replace(/\.mp4$/, `.${w}x${h}.mp4`) };
        return;
      }

      if (!this.bounds)
        return;

      const { width, height } = this.bounds;

      // Use a 9x16 source if appropriate (this aspect ratio is common when used with the outline-phone component)
      const landscape = width / height > .6;

      const scales = landscape ? SCALES : SCALES.slice(1).map(([ w, h ]) => [ h, w ]);
      const [ w, h ] = scales.find(([ w, h ]) => (width < w) && (height < h)) || [ null, null ];
      if (!this.scaled || (this.scaled.landscape != landscape) || (w > this.scaled.w)) {
        const src = w ? this.src.replace(/\.mp4$/, `.${w}x${h}.mp4`) : this.src;
        this.scaled = { src, landscape, w, h };
      }
    }
  },

  watch: {
    src() {
      this.loaded = false;
    },

    updateCondition: {
      immediate: true,
      handler() {
        this.update();
      }
    },

    async scaled(v) {
      this.tryVideo = true;
      await this.$nextTick();
      if (v && (this.$refs.video?.src == v.src))
        this.$refs.video.play().catch(
          e => {
            if (this.$refs.video?.src == v.src)
              this.tryVideo = false;
          }
        )
    },

    loaded() {
      // Warning: this can actually fire multiple times if the screen gets resized
      this.$emit('loaded');
    }
  }
}
</script>
